import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTicket, submitMoreInfo } from '../utils/api';
import { getFieldRequest, updateFieldRequestStatus, deleteFieldRequest } from '../utils/offlineCache';
import EscalationSummary from '../components/EscalationSummary';
import { getFaultCodeReferenceLinks } from '../utils/sourceRegistry';

const STATUS_LABELS = {
  triaged: 'Triage done',
  evidence_collecting: 'Collecting evidence',
  evidence_submitted: 'Evidence submitted',
  escalation_pending: 'Awaiting approval',
  escalated: 'Awaiting approval',
  more_info_requested: 'More info requested',
  completed: 'Completed',
  approved: 'Approved',
  rejected: 'Rejected',
  pending_sync: 'Pending sync',
};

function FieldTicketDetail({ isOnline: isOnlineProp }) {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [moreInfoNotes, setMoreInfoNotes] = useState('');
  const [moreInfoPhotos, setMoreInfoPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isOnline = isOnlineProp !== undefined ? isOnlineProp : (typeof navigator !== 'undefined' && navigator.onLine);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setLoadError(null);
      try {
        if (isOnline) {
          try {
            const res = await getTicket(ticketId);
            if (!cancelled) {
              setTicket(res.ticket);
              if (res.ticket?.status) {
                updateFieldRequestStatus(ticketId, res.ticket.status).catch(() => {});
              }
            }
          } catch (apiErr) {
            const cached = await getFieldRequest(ticketId);
            if (!cancelled) {
              if (cached) {
                setTicket(cached);
                setLoadError('Loaded from local cache — server unavailable.');
              } else {
                setTicket(null);
                setLoadError(apiErr.message || 'Could not load ticket.');
              }
            }
          }
        } else {
          const cached = await getFieldRequest(ticketId);
          if (!cancelled) {
            setTicket(cached);
            if (!cached) setLoadError('Ticket not found in local cache.');
          }
        }
      } catch (e) {
        if (!cancelled) {
          setTicket(null);
          setLoadError(e.message || 'Could not load ticket.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [ticketId, isOnline]);

  const handleMoreInfoPhotoChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const max = 5;
    const imageFiles = files.filter((f) => f.type.startsWith('image/')).slice(0, max);
    imageFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setMoreInfoPhotos((prev) => {
          const next = [...prev, reader.result].slice(0, max);
          return next;
        });
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeMoreInfoPhoto = (index) => {
    setMoreInfoPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmitMoreInfo = async () => {
    if (!moreInfoNotes.trim() || !ticketId) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);
    try {
      await submitMoreInfo(ticketId, moreInfoNotes.trim(), moreInfoPhotos);
      setMoreInfoNotes('');
      setMoreInfoPhotos([]);
      setSubmitSuccess(true);
      try {
        const res = await getTicket(ticketId);
        setTicket(res.ticket);
        await updateFieldRequestStatus(ticketId, 'escalation_pending').catch(() => {});
      } catch (refreshErr) {
        console.warn('Refresh after submit failed:', refreshErr);
      }
      setTimeout(() => setSubmitSuccess(false), 4000);
    } catch (err) {
      console.error('Submit more info failed:', err);
      const message = err?.message || (err?.detail ? (typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail)) : 'Could not send response. Check your connection and try again.');
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove this request from your list? This only removes it from this device.')) return;
    setDeleting(true);
    try {
      await deleteFieldRequest(ticketId);
      navigate('/field/requests');
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content page-content--mobile" style={{ textAlign: 'center', padding: 48 }}>
        Loading…
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="page-content page-content--mobile">
        <p>{loadError || 'Ticket not found.'}</p>
        <button type="button" className="btn btn--outline" onClick={() => navigate('/field/requests')}>
          Back to Requests
        </button>
      </div>
    );
  }

  const escalation = ticket.escalation_summary_json
    ? (typeof ticket.escalation_summary_json === 'object'
        ? ticket.escalation_summary_json
        : JSON.parse(ticket.escalation_summary_json || 'null'))
    : null;
  const statusLabel = STATUS_LABELS[ticket.status] || ticket.status;
  const statusBadgeClass = (() => {
    const s = ticket.status;
    if (s === 'approved' || s === 'completed' || s === 'closed') return 'low';
    if (s === 'rejected') return 'high';
    if (s === 'more_info_requested') return 'medium';
    if (s === 'pending_sync') return 'medium';
    return (ticket.triage_risk_level || 'medium').toLowerCase();
  })();
  const needsMoreInfo = ticket.status === 'more_info_requested';
  const additionalInfo = (() => {
    const raw = ticket.additional_information_json;
    if (!raw) return [];
    try {
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      return [];
    }
  })();

  const evidenceResults = (() => {
    const raw = ticket.evidence_results_json;
    if (!raw) return [];
    try {
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch {
      return [];
    }
  })();

  return (
    <div className="page-content page-content--mobile" style={{ maxWidth: 800 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
        <button
          type="button"
          className="btn btn--outline btn--sm"
          onClick={() => navigate('/field/requests')}
        >
          ← Back to Requests
        </button>
        <button
          type="button"
          className="btn btn--outline btn--sm"
          onClick={handleDelete}
          disabled={deleting}
          style={{ color: 'var(--color-danger)' }}
        >
          {deleting ? 'Removing…' : 'Remove from list'}
        </button>
      </div>

      {loadError && (
        <div style={{
          background: 'var(--color-warning-bg)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: 'var(--radius-sm)',
          padding: '8px 12px',
          fontSize: 13,
          color: 'var(--color-warning)',
          marginBottom: 16,
        }}>
          {loadError}
        </div>
      )}

      {/* Ticket header */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card__header">
          <div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--color-accent)' }}>
              {ticket.ticket_id}
            </div>
            <div className="card__title" style={{ marginTop: 4 }}>
              {ticket.equipment_model} — {ticket.fault_code}
            </div>
            <div className="card__subtitle">
              {ticket.equipment_type} · {ticket.model_year} · {ticket.operating_hours} hrs
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            <span className={`badge badge--${(ticket.triage_risk_level || 'medium').toLowerCase()}`}>
              {ticket.triage_risk_level || 'Unknown'}
            </span>
            <span className={`badge badge--${statusBadgeClass}`} style={{ fontSize: 11 }}>
              {statusLabel}
            </span>
          </div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
          <strong>Symptoms:</strong> {ticket.symptoms}
        </div>
        {ticket.triage_category && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
            <strong>Category:</strong> {(ticket.triage_category || '').replace(/_/g, ' ')}
          </div>
        )}
        {ticket.fault_code && getFaultCodeReferenceLinks(ticket.fault_code).length > 0 && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>
            <strong>Fault code reference:</strong>{' '}
            {getFaultCodeReferenceLinks(ticket.fault_code).map((link, idx) => (
              <span key={idx}>
                {idx > 0 && ' · '}
                <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-accent)', fontWeight: 600 }}>
                  {link.name}
                </a>
              </span>
            ))}
          </div>
        )}
        {ticket.triage_confidence != null && (
          <div className="confidence-meter" style={{ marginTop: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Confidence</span>
            <div className="confidence-meter__bar">
              <div
                className="confidence-meter__fill"
                style={{
                  width: `${ticket.triage_confidence * 100}%`,
                  background: ticket.triage_confidence > 0.7
                    ? 'var(--color-safe)'
                    : ticket.triage_confidence > 0.5
                    ? 'var(--color-warning)'
                    : 'var(--color-danger)',
                }}
              />
            </div>
            <span className="confidence-meter__label">
              {(ticket.triage_confidence * 100).toFixed(0)}%
            </span>
          </div>
        )}
      </div>

      {/* Escalation summary — same nice layout as field escalation window */}
      {escalation && (
        <div style={{ marginBottom: 24 }}>
          <EscalationSummary
            escalation={escalation}
            ticket={ticket}
            onNewTicket={() => navigate('/field')}
            showNewTicketButton={false}
          />
        </div>
      )}

      {/* Evidence collected (steps + photos) */}
      {evidenceResults.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 className="card__title" style={{ marginBottom: 12 }}>Evidence collected</h3>
          {evidenceResults.map((step, idx) => (
            <div key={idx} className="evidence-collected-step">
              <div className="evidence-collected-step__header">
                <span className="evidence-collected-step__number">Step {step.step_number}</span>
                <span className="evidence-collected-step__action">{step.action}</span>
              </div>
              {step.result_value && (
                <div className="evidence-collected-step__notes">{step.result_value}</div>
              )}
              {step.photo_base64_list && step.photo_base64_list.length > 0 && (
                <div className="photo-thread" style={{ marginTop: 8 }}>
                  {step.photo_base64_list.map((dataUrl, i) => (
                    <img
                      key={i}
                      src={dataUrl}
                      alt={`Step ${step.step_number} photo ${i + 1}`}
                      className="photo-thread__img"
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Q&A thread: show conversation history */}
      {additionalInfo.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 className="card__title" style={{ marginBottom: 12 }}>Additional information (Q&A)</h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {additionalInfo.map((entry, idx) => (
              <li
                key={idx}
                style={{
                  marginBottom: 12,
                  padding: 12,
                  borderRadius: 'var(--radius-sm)',
                  background: entry.from === 'supervisor' ? 'var(--color-info-bg, #f0f7ff)' : 'var(--color-safe-bg, #f0fff4)',
                  borderLeft: `4px solid ${entry.from === 'supervisor' ? 'var(--color-accent)' : 'var(--color-safe)'}`,
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  {entry.from === 'supervisor' ? 'Supervisor' : 'You'} · {entry.at ? new Date(entry.at).toLocaleString() : ''}
                </div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{entry.message}</div>
                {entry.photos && entry.photos.length > 0 && (
                  <div className="photo-thread">
                    {entry.photos.map((dataUrl, i) => (
                      <img key={i} src={dataUrl} alt={`Attachment ${i + 1}`} className="photo-thread__img" />
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Supervisor requested more info: show their message and form to respond */}
      {needsMoreInfo && (
        <div className="card escalation-summary" style={{ marginBottom: 24 }}>
          <h3 className="escalation-summary__title">Supervisor requested more information</h3>
          {ticket.approval_notes && (
            <div className="escalation-summary__summary-box" style={{ marginBottom: 16, whiteSpace: 'pre-wrap' }}>
              {ticket.approval_notes}
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Your response</label>
            <textarea
              className="form-textarea"
              value={moreInfoNotes}
              onChange={(e) => {
                setMoreInfoNotes(e.target.value);
                if (submitError) setSubmitError(null);
              }}
              placeholder="Provide the additional details requested…"
              rows={4}
            />
          </div>
          <div className="form-group photo-upload">
            <span className="form-label">Add photos (optional)</span>
            <div className="photo-upload__wrap">
              <input
                id="photo-input-more-info"
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={handleMoreInfoPhotoChange}
                className="photo-upload__input photo-upload__input--hidden"
                aria-label="Attach photos"
              />
              <label htmlFor="photo-input-more-info" className="photo-upload__btn">
                Choose photos
              </label>
              {moreInfoPhotos.length > 0 && (
                <span className="photo-upload__hint">{moreInfoPhotos.length} selected</span>
              )}
            </div>
            {moreInfoPhotos.length > 0 && (
              <div className="photo-thumbnails">
                {moreInfoPhotos.map((dataUrl, i) => (
                  <div key={i} className="photo-thumbnail photo-thumbnail--lg">
                    <img src={dataUrl} alt={`Photo ${i + 1}`} className="photo-thumbnail__img" />
                    <button
                      type="button"
                      className="photo-thumbnail__remove"
                      onClick={() => removeMoreInfoPhoto(i)}
                      aria-label="Remove photo"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {submitError && (
            <div className="form-feedback form-feedback--error" role="alert">
              {submitError}
            </div>
          )}
          {submitSuccess && (
            <div className="form-feedback form-feedback--success" role="status">
              Response sent. It’s back in your supervisor’s queue.
            </div>
          )}
          <button
            type="button"
            className="btn btn--primary"
            disabled={submitting || !moreInfoNotes.trim()}
            onClick={handleSubmitMoreInfo}
            aria-busy={submitting}
            aria-disabled={submitting || !moreInfoNotes.trim()}
          >
            {submitting ? 'Sending…' : 'Send response'}
          </button>
          {!isOnline && (
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
              You need to be online to send a response.
            </p>
          )}
        </div>
      )}

      {/* Approval outcome when already decided */}
      {ticket.approval_action && ticket.status !== 'more_info_requested' && (
        <div
          className="card"
          style={{
            marginBottom: 24,
            borderColor: ticket.approval_action === 'approved' ? 'var(--color-safe)' : ticket.approval_action === 'rejected' ? 'var(--color-danger)' : undefined,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {ticket.approval_action === 'approved' && '✓ Approved'}
            {ticket.approval_action === 'rejected' && '✗ Rejected'}
            {ticket.approval_action === 'more_info_requested' && '? More info requested'}
          </div>
          {ticket.approved_by && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
              By {ticket.approved_by} at {ticket.approved_at}
            </div>
          )}
          {ticket.approval_notes && (
            <div style={{ fontSize: 13, marginTop: 8, whiteSpace: 'pre-wrap' }}>{ticket.approval_notes}</div>
          )}
        </div>
      )}
    </div>
  );
}

export default FieldTicketDetail;
